/**
 * SessionContext - Session management for regular users
 * Features:
 * - Multi-tab sync via digitalvault-session-user
 * - Inactivity timeout: 30 minutes
 * - Activity tracking
 * - Automatic logout on session expiry
 * - 401 interceptor
 */

import React, { createContext, useContext, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useInactivityTimeout } from '../hooks/useInactivityTimeout';
import InactivityWarning from '../components/InactivityWarning';
import logger from '../utils/logger';
import apiClient from '../utils/apiConfig';
import {
  initTabSync,
  stopTabSync,
  broadcastLogout,
  broadcastSessionExtended
} from '../utils/tabSync';
import { handleRemoteActivity } from '../utils/activityTracker';
import { getRoleChannelName, getRoleInactivityTimeout, SESSION_TYPES } from '../utils/sessionManager';

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const CHANNEL_NAME = getRoleChannelName(SESSION_TYPES.USER);
const INACTIVITY_TIMEOUT_MINUTES = getRoleInactivityTimeout(SESSION_TYPES.USER);

const INACTIVITY_TIMEOUT_ENABLED = import.meta.env.VITE_ENABLE_INACTIVITY_TIMEOUT !== 'false';
const TAB_SYNC_ENABLED = import.meta.env.VITE_ENABLE_TAB_SYNC !== 'false';

const SessionContext = createContext(null);

export const SessionProvider = ({ children, user, onLogout }) => {
  const handleInactivityLogout = useCallback(async () => {
    logger.log('User inactive - triggering auto logout');

    // Broadcast logout to other user tabs FIRST
    if (TAB_SYNC_ENABLED) {
      broadcastLogout('inactivity', CHANNEL_NAME);
    }

    toast.warning('You have been logged out due to inactivity.', {
      position: 'top-center',
      autoClose: 5000
    });

    try {
      const token = localStorage.getItem('token');
      if (token) {
        await axios.post(`${API}/logout`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
    } catch (error) {
      logger.error('Logout API call failed:', error);
    }

    if (onLogout) {
      onLogout();
    }

    window.location.href = '/signin';
  }, [onLogout]);

  const {
    showWarning,
    warningSeconds,
    handleStayLoggedIn,
    handleAutoLogout
  } = useInactivityTimeout(
    handleInactivityLogout,
    INACTIVITY_TIMEOUT_MINUTES,
    CHANNEL_NAME,
    INACTIVITY_TIMEOUT_ENABLED && !!user
  );

  // Setup 401 interceptor for both axios and apiClient
  useEffect(() => {
    const handle401Error = (error) => {
      if (error.response?.status === 401) {
        const requestUrl = error.config?.url || '';

        // Skip interceptor for auth endpoints (signin, signup, etc.)
        // These should handle their own error messages.
        // Nominee activation is pre-auth: a 401 there means wrong invitation
        // key / security answers, NOT an expired session — the activation page
        // must show its own error instead of being bounced to /signin.
        const isAuthEndpoint = requestUrl.includes('/auth/signin') ||
                               requestUrl.includes('/auth/signup') ||
                               requestUrl.includes('/admin/login') ||
                               requestUrl.includes('/admin') ||
                               requestUrl.includes('/nominee/login/') ||
                               requestUrl.includes('/nominee/activate/');

        // Non-critical calls fired during/just after login. A 401 here must NOT
        // nuke the brand-new session and bounce the user back to /signin (this
        // was hiding the post-login Nominee Required gate on first sign-in).
        const isNonCriticalLoginCall = requestUrl.includes('/profile/migrate-mobile');

        if (isAuthEndpoint || isNonCriticalLoginCall) {
          // Let the caller handle (or ignore) the error without a forced logout.
          return Promise.reject(error);
        }

        const detail = error.response?.data?.detail || error.response?.data?.message || 'unknown';
        const shortUrl = requestUrl.replace(/^https?:\/\/[^/]+/, '');
        logger.error(`401 forcing logout — endpoint=${shortUrl} detail=${detail}`);
        toast.error(`Session error (401) on ${shortUrl}: ${detail}`, {
          position: 'top-center',
          autoClose: 8000
        });

        if (TAB_SYNC_ENABLED) {
          broadcastLogout('session_expired', CHANNEL_NAME);
        }

        if (onLogout) {
          onLogout();
        }

        window.location.href = '/signin';
      }
      return Promise.reject(error);
    };

    // Interceptor for global axios instance (used by logout API call)
    const axiosInterceptor = axios.interceptors.response.use(
      response => response,
      handle401Error
    );

    // Interceptor for apiClient instance (main API client)
    const apiClientInterceptor = apiClient.interceptors.response.use(
      response => response,
      handle401Error
    );

    return () => {
      axios.interceptors.response.eject(axiosInterceptor);
      apiClient.interceptors.response.eject(apiClientInterceptor);
    };
  }, [onLogout]);

  // Setup tab sync
  useEffect(() => {
    if (!TAB_SYNC_ENABLED || !user) return;

    const tabSyncCallbacks = {
      // When another tab is active, sync the activity
      onActivity: (data) => {
        logger.log('[UserTabSync] Activity detected in another tab, syncing...');
        handleRemoteActivity(data);
      },

      // When another tab shows warning, just log it
      onWarning: (data) => {
        logger.log('[UserTabSync] Warning shown in another tab');
      },

      // When another tab logs out, logout this tab too
      onLogout: (data) => {
        logger.log('[UserTabSync] Logout detected in another tab, logging out...');
        toast.info('You have been logged out from another tab.', {
          position: 'top-center',
          autoClose: 3000
        });

        if (onLogout) {
          onLogout();
        }

        // Small delay to show toast
        setTimeout(() => {
          window.location.href = '/signin';
        }, 500);
      },

      // When another tab extends session, sync here
      onSessionExtended: () => {
        logger.log('[UserTabSync] Session extended in another tab, syncing...');
        handleRemoteActivity({ timestamp: Date.now() });
        toast.success('Session extended in another tab!', {
          position: 'top-center',
          autoClose: 2000
        });
      }
    };

    const syncInitialized = initTabSync(tabSyncCallbacks, CHANNEL_NAME);
    if (syncInitialized) {
      logger.log('[SessionProvider] Multi-tab sync enabled for user');
    }

    return () => {
      stopTabSync(CHANNEL_NAME);
    };
  }, [user, onLogout]);

  const handleStayLoggedInWithToast = useCallback(() => {
    handleStayLoggedIn();

    // Broadcast to other tabs
    if (TAB_SYNC_ENABLED) {
      broadcastSessionExtended(CHANNEL_NAME);
    }

    toast.success('Session extended!', {
      position: 'top-center',
      autoClose: 2000
    });
  }, [handleStayLoggedIn]);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    inactivityTimeout: INACTIVITY_TIMEOUT_MINUTES,
    channelName: CHANNEL_NAME,
    inactivityEnabled: INACTIVITY_TIMEOUT_ENABLED,
    tabSyncEnabled: TAB_SYNC_ENABLED
  }), [INACTIVITY_TIMEOUT_MINUTES, CHANNEL_NAME, INACTIVITY_TIMEOUT_ENABLED, TAB_SYNC_ENABLED]);

  return (
    <SessionContext.Provider value={contextValue}>
      {children}

      {INACTIVITY_TIMEOUT_ENABLED && (
        <InactivityWarning
          show={showWarning}
          secondsRemaining={warningSeconds}
          onStayLoggedIn={handleStayLoggedInWithToast}
          onLogout={handleAutoLogout}
          roleType="User"
        />
      )}
    </SessionContext.Provider>
  );
};

export const useSession = () => {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSession must be used within SessionProvider');
  }
  return context;
};
